import * as React from 'react';
import assertions from 'lib/assertions';
import Columns2 from 'components/Columns2';
// @ts-ignore
import Countries from 'lib/geo/Country';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import { UserDefinedOrganizationType } from 'globalTypes';
import { ORG_TYPE_OPTIONS } from 'admin/constants/AdminUpgrade';
// @ts-ignore
import InputValueConstants from 'constants/InputValueConstants';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import Radio from 'components/Radio';
import Field from 'components/Field';
import Label from 'components/Label';
import { badPostalCodeForCountry } from 'utils/selfServeHelpers';

import './styles/AdminUpgradePlanInfoInputForm.scss';

export type Props = {
  country?: string;
  postalCode?: string;
  organizationName?: string;
  userDefinedOrgType?: UserDefinedOrganizationType;
  studentsCount?: number;
  onUpdate: (state: FormFields, postalCodeProblem?: boolean) => void;
};

type FormFields = {
  country?: string;
  postalCode?: string;
  organizationName?: string;
  userDefinedOrgType?: UserDefinedOrganizationType;
  studentsCount?: number;
};

type State = {
  country?: string;
  postalCode?: string;
};

class AdminUpgradePlanOrgInfoSimple extends React.Component<Props, State> {
  readonly state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      country: props.country,
      postalCode: props.postalCode,
    };
  }

  updatePostalCodeOrCountry = (update: FormFields, noPostalCodeMatch?: boolean) => {
    this.setState(update);
    this.props.onUpdate(update, noPostalCodeMatch || false);
  };

  render = () => {
    return (
      <div data-test="about-org-form-simple">
        <Row>
          <Text weight="semibold" size="xlarge" element="div">
            Contact us
          </Text>
          <Text size="medium" element="div">
            Our team is always happy to chat!
          </Text>
        </Row>
        <Row>
          <Field>
            <Label>Organization type</Label>
            {ORG_TYPE_OPTIONS.map(option => (
              <Radio
                inline
                name="org"
                key={option.value}
                label={option.label}
                onChange={() => this.props.onUpdate({ userDefinedOrgType: option.value })}
              />
            ))}
          </Field>
        </Row>
        <Columns2>
          <FormElement
            label={'Organization name'}
            element={Input}
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.ORGANIZATION_NAME_LENGTH
              );
            }}
            onValidChange={e => this.props.onUpdate({ organizationName: e.target.value })}
            onInvalidChange={e => this.props.onUpdate({ organizationName: undefined })}
            elementProps={{
              'data-test-selector': 'organization-name-input',
            }}
          />
          <FormElement
            label={'Estimated student enrollment'}
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e =>
              this.props.onUpdate({ studentsCount: Number.parseInt(e.target.value, 10) })
            }
            onInvalidChange={() => this.props.onUpdate({ studentsCount: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.props.studentsCount,
              'data-test-selector': 'est-students-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={'Country'}
            validate={value => {
              assertions.notEmpty(value, 'Please select a country');
              assertions.validPostalCodeForCountry(this.state.postalCode || '', value);
            }}
            onValidChange={country => {
              const postalCodeError = badPostalCodeForCountry(this.state.postalCode, country);
              this.updatePostalCodeOrCountry(
                { country: postalCodeError ? undefined : country },
                postalCodeError
              );
            }}
            onInvalidChange={country => {
              const postalCodeError = badPostalCodeForCountry(this.state.postalCode, country);
              this.updatePostalCodeOrCountry({ country: undefined }, postalCodeError);
            }}
            element={Select}
            elementProps={{
              name: 'country',
              value: this.state.country,
              options: Countries,
              className: 'AdminUpgradePlanInfoInputForm-countryPicker',
            }}
          />
          <FormElement
            label={'Postal code'}
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.validPostalCodeForCountry(
                e.target.value,
                this.state.country || '',
                'Invalid postal code'
              );
            }}
            onValidChange={e => {
              const postalCodeError = badPostalCodeForCountry(e.target.value, this.state.country);

              this.updatePostalCodeOrCountry(
                { postalCode: postalCodeError ? undefined : e.target.value },
                postalCodeError
              );
            }}
            onInvalidChange={e => {
              const postalCodeError = badPostalCodeForCountry(e.target.value, this.state.country);
              this.updatePostalCodeOrCountry({ postalCode: undefined }, postalCodeError);
            }}
            element={Input}
            elementProps={{
              name: 'postal code',
              'data-test-selector': 'postal-code-input',
            }}
            manualError={
              badPostalCodeForCountry(this.state.postalCode || '', this.state.country || '')
                ? 'Invalid postal code'
                : undefined
            }
          />
        </Columns2>
      </div>
    );
  };
}

export { AdminUpgradePlanOrgInfoSimple };
export default AdminUpgradePlanOrgInfoSimple;
